import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { resetUserPassword } from "../../api/webUserAPI";
import { useTranslation } from "react-i18next";

export default function PasswordResetDialog({
  open,
  onClose,
  userId,
  onSnackbar,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ newPassword: "", confirmPassword: "" });
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setForm({ newPassword: "", confirmPassword: "" });
    onClose();
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.newPassword) {
      onSnackbar({
        open: true,
        message:
          t("users.messages.passwordRequired") || "Password is required",
        severity: "error",
      });
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      onSnackbar({
        open: true,
        message:
          t("users.messages.passwordMismatch") || "Passwords do not match",
        severity: "error",
      });
      return;
    }

    try {
      setLoading(true);
      const response = await resetUserPassword(userId, form.newPassword);

      if (response.returnCode === -1) {
        throw new Error(response.exMessage || "Reset password failed");
      }

      console.log("Password reset successfully for user ID:", userId);

      onSnackbar({
        open: true,
        message:
          t("users.messages.passwordResetSuccess") ||
          "Password has been reset successfully",
        severity: "success",
      });

      handleClose();
    } catch (error) {
      console.error("Error resetting password:", error);
      onSnackbar({
        open: true,
        message:
          t("users.messages.passwordResetFailed") +
          ": " +
          (error.message || "Unknown error"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("users.dialogs.resetPassword.title") || "Reset Password"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <TextField
            label={t("users.fields.newPassword") || "New Password"}
            type="password"
            value={form.newPassword}
            onChange={(e) => handleFormChange("newPassword", e.target.value)}
            fullWidth
            required
          />
          <TextField
            label={t("users.fields.confirmPassword") || "Confirm Password"}
            type="password"
            value={form.confirmPassword}
            onChange={(e) =>
              handleFormChange("confirmPassword", e.target.value)
            }
            fullWidth
            required
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t("common.cancel")}</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          color="primary"
        >
          {loading ? (
            <CircularProgress size={20} />
          ) : (
            t("users.actions.resetPassword") || "Reset Password"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
